import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons/UniButton';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniOtpInput } from '@/design-system/components/inputs';
import { ModalHeader } from '@/design-system/components/modal/ModalHeader';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { StackActions, useNavigation } from '@react-navigation/native';
import { useUtxUsersGetUserProfile } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import * as DocumentPicker from 'expo-document-picker';
import _, { isString } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, useWindowDimensions } from 'react-native';
import { RootStackRoutes } from 'types';
import { OtpInput } from '../../enum';
import { PAYMENT_TYPE, QuoteDataRequest, QuoteResponse, useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { useVerifyOtp } from '../../hooks/useTfaVerify.hook';
import { BillPaymentStackRoutes } from '../../payment.navigator';
import { errorParser } from './type';

export interface SmsConfirmationProps {
  request: QuoteDataRequest;
  response: QuoteResponse;
  debitAccount: string;
  onClose: () => void;
  flagUploaded?: boolean;
  paymentType: PAYMENT_TYPE;
  userFee: string;
  documentSelected?: DocumentPicker.DocumentResult;
}

export const SmsConfirmationWeb: React.FC<SmsConfirmationProps> = (props) => {
  const { request, response, debitAccount, onClose, paymentType, userFee, documentSelected } = props;
  const { t } = useTranslation('global-bill-payment');
  const navigation = useNavigation();
  const layout = useContext(LayoutContext);
  const { height: heightScreen } = useWindowDimensions();
  const { theme } = useDripsyTheme();
  const { $4, $20 } = theme.space || ({} as any);
  const {
    topHeader,
    body,
    viewRowButton,
    viewButton2,
    textReturn,
    viewDividerHorizontal,
    iconView,
    otpView,
    otpText,
    titleText,
    viewAlert
  } = sxStyles;
  const [sizeWidthButton, setSizeWithButton] = useState<number>(0);
  const [countdownTime, setCountdownTime] = useState<number>(15);
  const paddingVerticalContainer = 30;
  const {
    general: { alertMessage, setAlertMessage },
    postCreateBillPayment: { postSubmit, mapInstructionSubmitRequest, isLoadingSubmit }
  } = useCreateBillPayment();
  const {
    smsVerify: { postSendSmsVerificationApi }
  } = useVerifyOtp(request.id!);
  const queryProfile = useUtxUsersGetUserProfile({
    query: {
      enabled: false
    }
  });

  const [pageState, setPageState] = useState<{
    ready?: boolean;
    code?: string;
    errorMessage?: string;
    requestAgain?: boolean;
  }>({});

  const mobile = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.loginPhoneNumber;
    }
    return '';
  }, [queryProfile.data?.data]);

  const countryCode = useMemo(() => {
    if (queryProfile.data && queryProfile.data.data) {
      return queryProfile.data.data.countryCode;
    }
    return 852;
  }, [queryProfile.data?.data]);

  useEffect(() => {
    queryProfile.refetch();
    postSendSmsVerificationApi(
      (data) => undefined,
      (error) => undefined
    );
  }, []);

  useEffect(() => {
    let number = countdownTime;
    let status = pageState?.requestAgain;
    if (number === 0) {
      status = false;
      setPageState({ ...pageState, requestAgain: false });
      setCountdownTime(15);
    }
    var timer: ReturnType<typeof setTimeout>;
    if (number > 0 && status) {
      timer = setTimeout(() => {
        --number;
        setCountdownTime(number);
      }, 1000);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [pageState?.requestAgain, countdownTime]);

  const sendRequestAgain = () => {
    setPageState({ ...pageState, requestAgain: true });
    postSendSmsVerificationApi(
      (data) => undefined,
      (error) => undefined
    );
  };

  const onConfirm = (code?: string) => {
    setPageState({ ...pageState, errorMessage: '' });
    setAlertMessage('');
    let otpCode = pageState && pageState.code ? pageState.code : '';
    if (!pageState.ready) {
      setPageState({ ...pageState, errorMessage: t('global-bill-payment::otp.error--otp-invalid') });
    } else {
      postSubmit(
        mapInstructionSubmitRequest(request, response, code ?? otpCode),
        (submitData) => {
          onClose();
          setTimeout(() => {
            navigation.dispatch(
              StackActions.replace(RootStackRoutes.BillPayment, {
                screen: BillPaymentStackRoutes.Success,
                params: {
                  request,
                  response,
                  debitAccount,
                  submitResponse: submitData.data,
                  paymentType,
                  userFee,
                  documentSelected
                }
              })
            );
          }, 350);
        },
        (error) => {
          const data: any = error.response?.data;
          const errorMessage = data && isString(data.message) ? errorParser(data.message, t) : errorParser(error.message, t);
          setPageState({ ...pageState, errorMessage: errorMessage });
        }
      );
    }
  };

  useEffect(() => {
    if (pageState && pageState.code && pageState.ready) {
      onConfirm(pageState.code);
    }
  }, [pageState?.code, pageState?.ready]);

  const onCancel = () => {
    onClose();
  };

  const onLayout = (e: LayoutChangeEvent) => {
    const { width } = e.nativeEvent.layout;
    setSizeWithButton(width);
  };

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(400, layout.dimensions.width),
        maxHeight: heightScreen - paddingVerticalContainer
      }}>
      <View sx={topHeader}>
        <ModalHeader title={t('global-bill-payment::glossary.title--sms-authentication')} closeModal={onClose} bordered />
      </View>
      <ScrollView showsVerticalScrollIndicator={false}>
        <View sx={body}>
          <View sx={iconView}>
            <Icon name="sms2FA" height={$20} width={$20} />
          </View>
          <TextBody sx={titleText}>{t('global-bill-payment::sms.text--protecting-your-account')}</TextBody>
          <View sx={otpView}>
            <TextCaption semiBold sx={otpText}>
              {t('global-bill-payment::authentication.sms.text--enter-code')}
            </TextCaption>
            <UniOtpInput
              errorText={pageState && !_.isEmpty(pageState.errorMessage) ? pageState.errorMessage : undefined}
              maxLength={OtpInput.MAX_LENGTH}
              onValueChange={(code) => {
                setPageState({ ...pageState, code, ready: code.length === OtpInput.MAX_LENGTH });
              }}
            />
            <View sx={sxStyles.requestAgainView}>
              <TextCaption sx={sxStyles.secondaryColorText}>
                {t('log-in::sign-up.text--verification-code-will-be-sent-to')}{' '}
                <TextCaption semiBold sx={sxStyles.secondaryColorText}>
                  ({countryCode}){mobile.replace(mobile.substring(mobile.length - 6, mobile.length - 2), '****')}
                </TextCaption>
              </TextCaption>
              <View sx={sxStyles.requestNewCodeView}>
                <TextCaption sx={sxStyles.secondaryColorText}>
                  {t('log-in::sign-up.text--can-not-receive')}{' '}
                  {!pageState?.requestAgain && (
                    <TextCaption sx={sxStyles.textRequestAgain} onPress={() => sendRequestAgain()}>
                      {t('log-in::sign-up.text--request-again')}
                    </TextCaption>
                  )}
                  {pageState?.requestAgain && (
                    <TextCaption sx={sxStyles.requestAgainText}>
                      {t('log-in::sign-up.text--request-new-one')} {countdownTime} {t('log-in::sign-up.text--second')}
                    </TextCaption>
                  )}
                </TextCaption>
              </View>
            </View>
            {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={viewAlert} />}
          </View>
        </View>
      </ScrollView>
      <View sx={viewRowButton} onLayout={onLayout}>
        <UniButton
          variant={ButtonVariant.outline1}
          labelSx={textReturn}
          sx={{ ...viewButton2, ...{ width: (sizeWidthButton - $4) / 2 } }}
          label={t('global-bill-payment::glossary.back')}
          onPress={onCancel}
        />
        <View sx={viewDividerHorizontal} />
        <UniButton
          sx={{ ...viewButton2, ...{ width: (sizeWidthButton - $4) / 2 } }}
          label={t('global-bill-payment::glossary.confirm')}
          onPress={() => onConfirm(pageState.code)}
          loading={isLoadingSubmit}
          disabled={isLoadingSubmit || !pageState.ready}
        />
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    paddingX: '$4',
    backgroundColor: '$backgroundPrimary'
  },
  topHeader: {
    height: '$17',
    justifyContent: 'center',
    zIndex: 3
  },
  body: {
    pt: '$7',
    px: '$7',
    zIndex: 2,
    alignItems: 'center'
  },
  iconView: {
    height: '$20',
    width: '$20',
    mb: '$2'
  },
  otpView: {
    mt: '$10'
  },
  titleText: {
    textAlign: 'center'
  },
  otpText: {
    mb: '$1'
  },
  viewRowButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    mt: '$10',
    mb: '$7',
    mx: '$7'
  },
  viewButton2: {
    width: '$60',
    height: '$11'
  },
  textReturn: {
    color: '$textPrimary'
  },
  viewDividerHorizontal: {
    width: '$4'
  },
  requestAgainView: {
    alignItems: 'center',
    mt: '$3'
  },
  requestNewCodeView: {
    width: '77%',
    alignItems: 'center',
    mt: '$5'
  },
  requestAgainText: {
    ml: '$2',
    mt: '$5',
    textAlign: 'center',
    color: '$textSecondary'
  },
  secondaryColorText: {
    color: '#666666'
  },
  textRequestAgain: {
    color: '#DC9B32'
  },
  viewAlert: {
    mt: '$3'
  }
};
